// src/pages/salary/styles/salaryStyles.js

/**
 * Build stats array for salary dashboard
 * @param {Object} payrollData - Payroll data object
 * @param {Object} calculatedValues - Calculated values object
 * @param {Object} t - Translations object
 * @param {Object} icons - Icons object with { DollarSign, Calculator, Shield, Award }
 * @returns {Array} Stats array for StatsGrid component
 */
export const buildSalaryStats = (payrollData, calculatedValues, t, icons) => [
  {
    icon: icons?.DollarSign || null,
    value: `${parseFloat(payrollData?.grossSalary || 0).toFixed(1)} ${t?.egp || 'EGP'}`,
    label: t?.grossSalary || 'Gross Salary',
    subtitle: t?.totalEarnings || 'Total Earnings',
    color: '#2e7cba',
  },
  {
    icon: icons?.Calculator || null,
    value: `${parseFloat(payrollData?.monthlyTax || 0).toFixed(1)} ${t?.egp || 'EGP'}`,
    label: t?.incomeTax || 'Income Tax',
    subtitle: t?.monthlyTaxDeduction || 'Monthly Tax Deduction',
    color: '#2e7cba',
  },
  {
    icon: icons?.Shield || null,
    value: `${parseFloat(calculatedValues?.socialInsurance || 0).toFixed(1)} ${t?.egp || 'EGP'}`,
    label: t?.socialInsurance || 'Social Insurance',
    subtitle: t?.employeeContribution || 'Employee Contribution',
    color: '#2e7cba',
  },
  {
    icon: icons?.Award || null,
    value: `${parseFloat(payrollData?.netSalary || 0).toFixed(1)} ${t?.egp || 'EGP'}`,
    label: t?.netSalary || 'Net Salary',
    subtitle: t?.takeHomePay || 'Take Home Pay',
    color: '#2e7cba',
  },
];

/**
 * Build stats grid styles configuration
 * @param {Object} styles - Styles object from getSalaryStyles
 * @param {Object} theme - Current theme (dark/light)
 * @param {Boolean} isRTL - Is RTL layout
 * @returns {Object} Stats grid styles configuration
 */
export const buildStatsGridStyles = (styles, theme, isRTL) => ({
  statsGrid: styles.statsGrid,
  statCard: {
    ...styles.card1,
    marginBottom: 0,
    textAlign: isRTL ? 'right' : 'left',
  },
  statHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
    flexDirection: isRTL ? 'row-reverse' : 'row',
  },
  statIconWrapper: {
    width: 'clamp(36px, 5vw, 44px)',
    height: 'clamp(36px, 5vw, 44px)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#2e7cba',
  },
  statTitle: {
    color: theme === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(26,26,26,0.7)',
    fontSize: 'clamp(13px, 1.8vw, 15px)',
    fontWeight: '600',
    margin: 0,
  },
  statValue: {
    color: '#2e7cba',
    fontSize: 'clamp(24px, 4vw, 32px)',
    fontWeight: '800',
    margin: 'clamp(8px, 1.5vw, 12px) 0',
  },
  statSubtitle: {
    color: theme === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(26,26,26,0.6)',
    fontSize: 'clamp(12px, 1.5vw, 14px)',
    margin: 0,
  },
});

export const getSalaryStyles = (theme, isRTL, screenSize) => {
  const { isMobile, isTablet, isLargeDesktop } = screenSize;
  const isDark = theme === 'dark';

  const cardBase = {
    background:      isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.9)',
    border:          isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.05)',
    boxShadow:       isDark ? '0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)' : '0 0.5rem 2rem rgba(0,0,0,0.1)',
    WebkitBackdropFilter: isDark ? 'blur(25px) saturate(180%)' : 'blur(10px)',
    transition:      'all 0.3s ease',
  };

  return {
    container: {
      marginTop: 0,
      direction: isRTL ? 'rtl' : 'ltr',
    },

    backgroundElements: {
      position: 'absolute', inset: '0',
      overflow: 'hidden', pointerEvents: 'none', zIndex: 0,
    },

    circle1: {
      position: 'absolute', top: '-200px',
      [isRTL ? 'left' : 'right']: '-200px',
      width: '500px', height: '500px',
      background: isDark
        ? 'radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)'
        : 'radial-gradient(circle, rgba(59,130,246,0.05) 0%, transparent 70%)',
      borderRadius: '50%', filter: 'blur(100px)',
    },

    circle2: {
      position: 'absolute', bottom: '-250px',
      [isRTL ? 'right' : 'left']: '-250px',
      width: '600px', height: '600px',
      background: isDark
        ? 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)'
        : 'radial-gradient(circle, rgba(139,92,246,0.05) 0%, transparent 70%)',
      borderRadius: '50%', filter: 'blur(120px)',
    },

    mainContent: {
      maxWidth:   isLargeDesktop ? '1400px' : '1200px',
      margin:     '0 auto',
      padding:    isMobile ? '16px 0' : isTablet ? '24px 0' : '32px 0',
      position:   'relative',
      zIndex:     1,
      marginTop:  '-2.5%',
    },

    headerCard: {
      ...cardBase,
      borderRadius:  isMobile ? '12px' : '20px',
      textAlign:     'center',
      marginTop:     '-30px',
      marginBottom:  '2%',
      padding:       isMobile ? '20px' : '5%',
      direction:     isRTL ? 'rtl' : 'ltr',
      cursor:        'pointer',
    },

    headerTitle: {
      color:      isDark ? 'white' : '#1a1a1a',
      fontWeight: '700',
      textShadow: isDark ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
      lineHeight: '1.2',
      fontSize:   isMobile ? '20px' : isTablet ? '24px' : '28px',
      margin:     0,
    },

    headerSubtitle: {
      color:      isDark ? 'rgba(255,255,255,0.8)' : 'rgba(26,26,26,0.7)',
      margin:     '8px 0 0 0',
      lineHeight: '1.4',
      fontSize:   isMobile ? '14px' : '16px',
    },

    filterCard: {
      ...cardBase,
      borderRadius:  isMobile ? '12px' : '20px',
      padding:       isMobile ? '16px' : '20px',
      marginTop:     '16px',
      marginBottom:  '24px',
      direction:     isRTL ? 'rtl' : 'ltr',
      cursor:        'pointer',
    },

    filterRow: {
      display:         'flex',
      alignItems:      'center',
      gap:             '12px',
      flexWrap:        'wrap',
      justifyContent:  isRTL ? 'flex-end' : 'flex-start',
    },

    filterLabel: {
      color:      isDark ? '#ffffff' : '#0f172a',
      fontSize:   'clamp(14px, 2vw, 16px)',
      fontWeight: '600',
    },

    printButton: {
      padding:         '8px 16px',
      borderRadius:    '8px',
      border:          'none',
      background:      '#2e7cba',
      color:           'white',
      fontSize:        '14px',
      cursor:          'pointer',
      display:         'flex',
      alignItems:      'center',
      gap:             '8px',
      fontWeight:      '600',
      marginLeft:      isRTL ? '0' : 'auto',
      marginRight:     isRTL ? 'auto' : '0',
    },

    statsGrid: {
      display:               'grid',
      gridTemplateColumns:   isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap:                   isMobile ? '12px' : isTablet ? '16px' : '24px',
      marginBottom:          isMobile ? '20px' : '32px',
    },

    detailsGrid: {
      display:               'grid',
      gridTemplateColumns:   isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
      gap:                   isMobile ? '12px' : '20px',
    },

    detailCard: {
      padding:    isMobile ? '16px' : '24px',
      background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
      borderRadius: isMobile ? '12px' : '16px',
      border:     '2px solid rgba(46,124,186,0.2)',
      transition: 'all 0.3s ease',
      direction:  isRTL ? 'rtl' : 'ltr',
      textAlign:  isRTL ? 'right' : 'left',
      cursor:     'pointer',
    },

    detailHeader: {
      display:         'flex',
      alignItems:      'center',
      justifyContent:  'space-between',
      marginBottom:    '16px',
    },

    detailIcon: {
      width:           isMobile ? '40px' : '48px',
      height:          isMobile ? '40px' : '48px',
      background:      'rgba(46,124,186,0.2)',
      borderRadius:    '12px',
      display:         'flex',
      alignItems:      'center',
      justifyContent:  'center',
      color:           '#2e7cba',
    },

    detailRate: {
      color:      '#2e7cba',
      fontSize:   'clamp(12px, 1.5vw, 14px)',
      fontWeight: '600',
      padding:    '4px 12px',
      background: 'rgba(46,124,186,0.15)',
      borderRadius: '20px',
    },

    detailTitle: {
      color:      isDark ? '#ffffff' : '#0f172a',
      fontSize:   'clamp(14px, 2vw, 16px)',
      fontWeight: '600',
      margin:     '0 0 8px 0',
    },

    detailAmount: {
      color:      '#2e7cba',
      fontSize:   'clamp(18px, 3vw, 20px)',
      fontWeight: '700',
      margin:     '0 0 12px 0',
    },

    detailDescription: {
      color:      isDark ? 'rgba(255,255,255,0.7)' : 'rgba(26,26,26,0.6)',
      fontSize:   'clamp(12px, 1.5vw, 14px)',
      margin:     0,
      lineHeight: '1.4',
    },

    compositionGrid: {
      display:               'grid',
      gridTemplateColumns:   isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
      gap:                   isMobile ? '12px' : '20px',
    },

    compositionCard: {
      padding:      isMobile ? '16px' : '20px',
      background:   isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
      borderRadius: isMobile ? '12px' : '16px',
      textAlign:    'center',
      border:       '2px solid rgba(46,124,186,0.2)',
      transition:   'all 0.3s ease',
      direction:    isRTL ? 'rtl' : 'ltr',
      cursor:       'pointer',
    },

    compositionIcon: {
      width:           isMobile ? '50px' : '60px',
      height:          isMobile ? '50px' : '60px',
      background:      'rgba(46,124,186,0.2)',
      borderRadius:    '50%',
      display:         'flex',
      alignItems:      'center',
      justifyContent:  'center',
      margin:          '0 auto 16px',
      color:           '#2e7cba',
    },

    compositionTitle: {
      color:      isDark ? '#ffffff' : '#0f172a',
      fontSize:   'clamp(14px, 2vw, 16px)',
      fontWeight: '600',
      margin:     '0 0 8px 0',
    },

    compositionAmount: {
      color:      '#2e7cba',
      fontSize:   'clamp(18px, 3vw, 20px)',
      fontWeight: '700',
      margin:     '0 0 8px 0',
    },

    compositionPercentage: {
      color:    isDark ? 'rgba(255,255,255,0.7)' : 'rgba(26,26,26,0.6)',
      fontSize: 'clamp(11px, 1.5vw, 12px)',
      margin:   '8px 0 0 0',
    },

    summaryGrid: {
      display:               'grid',
      gridTemplateColumns:   isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
      gap:                   isMobile ? '12px' : '20px',
      padding:               isMobile ? '16px' : '20px',
      background:            'rgba(59,130,246,0.1)',
      borderRadius:          isMobile ? '12px' : '16px',
    },

    summaryItem: { textAlign: 'center', direction: isRTL ? 'rtl' : 'ltr' },

    summaryValue: {
      color:        isDark ? '#ffffff' : '#0f172a',
      fontSize:     isMobile ? '20px' : isTablet ? '24px' : '32px',
      fontWeight:   '700',
      marginBottom: '8px',
    },

    summaryLabel: {
      color:      isDark ? 'rgba(255,255,255,0.8)' : 'rgba(26,26,26,0.7)',
      fontSize:   'clamp(12px, 1.5vw, 14px)',
      fontWeight: '500',
    },

    loadingContainer: {
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      minHeight:      '400px',
      gap:            '16px',
      direction:      isRTL ? 'rtl' : 'ltr',
    },

    loadingText: {
      color:    isDark ? 'rgba(255,255,255,0.8)' : 'rgba(26,26,26,0.7)',
      fontSize: 'clamp(14px, 2vw, 16px)',
    },

    errorBox: {
      background:   isDark ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.1)',
      border:       '2px solid #2e7cba',
      borderRadius: '16px',
      padding:      '24px',
      textAlign:    isRTL ? 'right' : 'center',
      direction:    isRTL ? 'rtl' : 'ltr',
    },

    errorText: {
      color:      '#2e7cba',
      fontSize:   'clamp(14px, 2vw, 16px)',
      fontWeight: '600',
    },
    card1: {
        background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
        WebkitBackdropFilter: theme === "dark" ? "blur(25px) saturate(180%)" : "blur(10px)",
        border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.05)",
        borderRadius: screenSize.isMobile ? "12px" : screenSize.isTablet ? "20px" : "28px",
        padding: screenSize.isMobile ? "16px" : screenSize.isTablet ? "20px" : "28px",
        boxShadow: "none",
        marginBottom: screenSize.isMobile ? "16px" : screenSize.isTablet ? "20px" : "24px",
        transition: "all 0.3s ease",
        cursor: "pointer",
        direction: isRTL ? "rtl" : "ltr",
    },

    emptyBox: {
      background:   isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
      border:       isDark ? '2px solid rgba(255,255,255,0.15)' : '2px solid rgba(0,0,0,0.1)',
      borderRadius: '16px',
      padding:      '24px',
      textAlign:    isRTL ? 'right' : 'center',
      direction:    isRTL ? 'rtl' : 'ltr',
    },
  };
};

export const getHoverStyles = (theme, isMobile) => ({
  transform:  isMobile ? 'scale(1.02)' : 'translateY(-2px)',
  boxShadow:  theme === 'dark'
    ? '0 16px 48px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
    : '0 16px 48px rgba(0,0,0,0.15)',
  background: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.95)',
  border:     theme === 'dark' ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(0,0,0,0.08)',
});